import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { FairSearchV2ProductCard } from '@/widgets/search/fair-v2/fair-search-v2.data'

/**
 * Gift quiz — open / close, and the last set of ideas for the Gift ideas page. There is no
 * saved "gift set" on the server (BE-153), so the page reads the set from the session.
 */
const RESULT_KEY = 'fair.gift.result'

export interface GiftQuizResult {
  recipientName: string
  chips: string[]
  cards: FairSearchV2ProductCard[]
}

function readResult(): GiftQuizResult | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(RESULT_KEY)
    return raw ? (JSON.parse(raw) as GiftQuizResult) : null
  } catch {
    return null
  }
}

export const useGiftQuizStore = defineStore('giftQuiz', () => {
  const isOpen = ref(false)
  /** Bumped on every open so the quiz re-reads People. */
  const session = ref(0)
  /** People id chosen before the quiz (the hub's person chip). */
  const presetPersonId = ref<number | null>(null)
  const result = ref<GiftQuizResult | null>(readResult())

  function open(personId: number | null = null) {
    presetPersonId.value = personId
    session.value += 1
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  function setResult(value: GiftQuizResult) {
    result.value = value
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem(RESULT_KEY, JSON.stringify(value))
    } catch {
      // Quota — the page still has it in memory until a reload.
    }
  }

  return { isOpen, session, presetPersonId, result, open, close, setResult }
})
